import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import AddressSearchInput from './AddressSearchInput';
import CcEmailsInput from './CcEmailsInput';
import { FILTER_CONFIG } from '../config/filters';
import { API_URL } from '../config/api';
import { Mail, Info } from 'lucide-react';
import { trackBuyBoxEvent, trackConversionEvent } from '../utils/analytics';
import './BuyBoxModal.css';

const BuyBoxModal = ({ isOpen, onClose, onSuccess }) => {
    const { session } = useAuth();
    const { showToast } = useToast();
    const navigate = useNavigate();

    const [activeTab, setActiveTab] = useState('manual'); // 'smart' or 'manual'
    const [name, setName] = useState('');
    const [emailFreq, setEmailFreq] = useState('daily');
    const [filters, setFilters] = useState({});
    const [saving, setSaving] = useState(false);

    // CC emails state
    const [ccEmails, setCcEmails] = useState([]);

    // Track buy box modal open - MOVE HOOK BEFORE CONDITIONAL RETURN
    useEffect(() => {
        if (isOpen) {
            trackBuyBoxEvent('initiated');
            trackConversionEvent('buy_box_setup');
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const handleReset = () => {
        setFilters({});
        setName('');
        setEmailFreq('daily');
        setCcEmails([]);

        // Track buy box abandonment when user resets
        trackBuyBoxEvent('abandoned', { reason: 'user_reset' });
    };

    const handleSubmit = async () => {
        if (!name.trim()) {
            showToast('Please give your Buy Box a name.', 'error');
            return;
        }

        setSaving(true);
        try {
            const response = await fetch(`${API_URL}/buy-boxes`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session?.access_token}`
                },
                body: JSON.stringify({
                    name,
                    criteria: filters,
                    email_frequency: emailFreq,
                    cc_emails: ccEmails
                })
            });

            if (response.ok) {
                // Track successful buy box creation
                const buyBoxData = {
                    criteriaCount: Object.keys(filters).length,
                    priceMin: filters.min_price,
                    priceMax: filters.max_price,
                    locations: [filters.city, filters.state, filters.zip_code].filter(Boolean),
                };
                trackBuyBoxEvent('completed', buyBoxData);
                
                showToast('Buy Box created!', 'success');
                handleReset();
                onSuccess?.();
            } else {
                // Track failed buy box creation
                trackBuyBoxEvent('abandoned', { reason: 'api_error' });
                showToast('Failed to create Buy Box', 'error');
            }
        } catch (error) {
            console.error(error);
            // Track error during buy box creation
            trackBuyBoxEvent('abandoned', { reason: 'exception' });
            showToast('Error creating Buy Box', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleSearchWithCriteria = () => {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
        navigate(`/search?${params.toString()}`);
        onClose();
    };

    // --- Render Helper ---
    const renderFilterInput = (config) => {
        if (config.id === 'sort') return null;

        return (
            <div key={config.id} className={`form-group ${config.type === 'checkbox_group' ? 'full-width' : ''}`}>
                <label className="form-label">{config.label}</label>

                {config.type === 'input' && (
                    <input
                        type={config.inputType || 'text'}
                        className="std-input"
                        placeholder={config.placeholder}
                        value={filters[config.key] || ''}
                        onChange={(e) => handleFilterChange(config.key, e.target.value)}
                    />
                )}

                {config.type === 'range' && (
                    <div className="range-inputs">
                        <input
                            type="number"
                            className="std-input"
                            placeholder="Min"
                            value={filters[config.minKey] || ''}
                            onChange={(e) => handleFilterChange(config.minKey, e.target.value)}
                        />
                        <span>-</span>
                        <input
                            type="number"
                            className="std-input"
                            placeholder="Max"
                            value={filters[config.maxKey] || ''}
                            onChange={(e) => handleFilterChange(config.maxKey, e.target.value)}
                        />
                    </div>
                )}

                {config.type === 'pills' && (
                    <div className="pills-group">
                        {config.options.map(opt => (
                            <button
                                key={opt.value}
                                className={`pill-btn ${filters[config.key] === opt.value ? 'active' : ''}`}
                                onClick={() => handleFilterChange(config.key, filters[config.key] === opt.value ? '' : opt.value)}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                )}

                {config.type === 'checkbox_group' && (
                    <div className="checkbox-group">
                        {config.options.map(opt => (
                            <label key={opt.value} className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={(filters[config.key] || '').split(',').includes(opt.value)}
                                    onChange={(e) => {
                                        const val = opt.value;
                                        let current = (filters[config.key] || '').split(',').filter(Boolean);
                                        if (e.target.checked) current.push(val);
                                        else current = current.filter(c => c !== val);
                                        handleFilterChange(config.key, current.join(','));
                                    }}
                                />
                                {opt.label}
                            </label>
                        ))}
                    </div>
                )}

                {config.type === 'keyword_input' && (
                    <div className="keyword-input-section">
                        <div className="keyword-input-wrapper">
                            <input
                                type="text"
                                className="std-input keyword-search-input"
                                placeholder={config.placeholder}
                                value={filters[config.key] || ''}
                                onChange={(e) => handleFilterChange(config.key, e.target.value)}
                            />
                            {filters[config.key] && (
                                <button
                                    className="keyword-clear-btn"
                                    onClick={() => handleFilterChange(config.key, '')}
                                >
                                    ✕
                                </button>
                            )}
                        </div>
                        <div className="keyword-chips-grid">
                            {config.options.map(opt => {
                                const currentVal = filters[config.key] || '';
                                const currentArray = currentVal.split(',').map(s => s.trim()).filter(Boolean);
                                const isSelected = currentArray.includes(opt.value);
                                return (
                                    <button
                                        key={opt.value}
                                        className={`keyword-chip ${isSelected ? 'selected' : ''}`}
                                        onClick={() => {
                                            let next;
                                            if (isSelected) {
                                                next = currentArray.filter(s => s !== opt.value);
                                            } else {
                                                next = [...currentArray, opt.value];
                                            }
                                            handleFilterChange(config.key, next.join(', '));
                                        }}
                                    >
                                        {isSelected ? <span className="chip-icon check">✓</span> : <span className="chip-icon plus">+</span>}
                                        {opt.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="buy-box-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2 className="modal-title">Create Buy Box</h2>
                    <button className="close-btn" onClick={onClose}>&times;</button>
                </div>

                {/* Tabs */}
                <div className="buy-box-tabs">
                    <button
                        className={`tab-btn ${activeTab === 'smart' ? 'active' : ''}`}
                        onClick={() => setActiveTab('smart')}
                    >
                        Smart Builder ✨
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'manual' ? 'active' : ''}`}
                        onClick={() => setActiveTab('manual')}
                    >
                        Manual Criteria
                    </button>
                </div>

                <div className="modal-content-scroll">
                    {activeTab === 'smart' ? (
                        <div className="smart-placeholder">
                            <div style={{ fontSize: '32px', marginBottom: '8px' }}>🤖</div>
                            <p>AI Smart Builder coming soon.</p>
                        </div>
                    ) : (
                        <div className="form-grid">
                            <div className="form-group full-width">
                                <label className="form-label">Name *</label>
                                <input
                                    type="text"
                                    className="std-input"
                                    placeholder="e.g. Detroit Flips"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    autoFocus
                                />
                            </div>

                            {/* Notification Section */}
                            <div className="form-group full-width notification-section">
                                <div className="notification-section-header">
                                    <Mail size={16} />
                                    <span>Notifications</span>
                                </div>
                                <div className="notification-row">
                                    <div className="notification-field">
                                        <label className="form-label">Email Frequency</label>
                                        <select
                                            className="std-select"
                                            value={emailFreq}
                                            onChange={(e) => setEmailFreq(e.target.value)}
                                        >
                                            <option value="daily">Daily</option>
                                            <option value="weekly">Weekly</option>
                                            <option value="monthly">Monthly</option>
                                            <option value="never">Never</option>
                                        </select>
                                    </div>
                                    <div className="notification-field">
                                        <CcEmailsInput
                                            value={ccEmails}
                                            onChange={setCcEmails}
                                        />
                                    </div>
                                </div>
                                <p className="notification-hint">
                                    <Info size={13} />
                                    CC recipients will receive the same property alert emails as you.
                                </p>
                            </div>

                            <div className="form-group full-width">
                                <label className="form-label">Location</label>
                                <AddressSearchInput
                                    variant="modal"
                                    showAddresses={false}
                                    displayValue={
                                        filters.city
                                            ? `${filters.city}, ${filters.state || ''}`
                                            : filters.zip_code || ''
                                    }
                                    onSelect={(sel) => {
                                        if (sel.type === 'city') {
                                            handleFilterChange('city', sel.city);
                                            handleFilterChange('state', sel.state);
                                            handleFilterChange('zip_code', '');
                                        }
                                        if (sel.type === 'zip') {
                                            handleFilterChange('zip_code', sel.zip_code);
                                            handleFilterChange('city', '');
                                            handleFilterChange('state', '');
                                        }
                                    }}
                                    onClear={() => {
                                        handleFilterChange('city', '');
                                        handleFilterChange('state', '');
                                        handleFilterChange('zip_code', '');
                                    }}
                                />
                            </div>

                            {FILTER_CONFIG.filter(c => c.id !== 'location').map(renderFilterInput)}
                        </div>
                    )}
                </div>

                <div className="modal-actions">
                    <button className="btn-secondary" onClick={onClose}>Cancel</button>
                    <button className="btn-secondary" onClick={handleSearchWithCriteria}>Search</button>
                    <button
                        className="btn-primary"
                        onClick={handleSubmit}
                        disabled={saving || (activeTab === 'smart')}
                    >
                        {saving ? 'Creating...' : 'Create Buy Box'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BuyBoxModal;
